import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { PageShell, ListingCard } from "@/components/common";
import { PhotoUploader } from "@/components/PhotoUploader";
import { CitySelect } from "@/components/DeliveryQuoteForm";
import { useMe } from "@/context/MeContext";

const EMPTY = { type: "sale", title: "", category: "", condition: "", description: "", price: "", city: "", exact_address: "", photos: [], weight_kg: "" };

function SimpleSelect({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Publish() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const { meta, me } = useMe();
  const [f, setF] = useState({ ...EMPTY, type: params.get("type") === "donation" ? "donation" : "sale" });
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (id) {
      api.get(`/listings/${id}`).then(({ data }) => setF({ ...EMPTY, ...data, price: data.price, weight_kg: data.weight_kg ?? "", exact_address: data.exact_address || "" }));
    }
  }, [api, id]);
  useEffect(() => {
    if (!id && me?.city) {
      setF((s) => (s.city ? s : { ...s, city: me.city }));
    }
  }, [me, id]);

  const body = () => ({
    type: f.type, title: f.title, category: f.category, condition: f.condition, description: f.description,
    price: f.type === "donation" ? 0 : Number(f.price || 0), city: f.city, exact_address: f.exact_address,
    photos: f.photos, weight_kg: f.weight_kg === "" ? null : Number(f.weight_kg),
  });

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = id ? await api.patch(`/listings/${id}`, body()) : await api.post("/listings", body());
      toast.success(data.moderation_status === "pending" ? "Annonce envoyée en modération" : "Annonce publiée !");
      navigate(`/annonce/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <PageShell eyebrow={id ? "Modifier" : "Publication gratuite"} title={id ? "Modifier l'annonce" : "Publier une annonce"}>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8 bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
          <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-full" data-testid="publish-type-toggle">
            {[["sale", "Vendre"], ["donation", "Donner"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`publish-type-${v}`} onClick={() => set("type")(v)} className={`rounded-full py-2.5 text-sm font-semibold transition-colors ${f.type === v ? (v === "sale" ? "bg-forest text-white" : "bg-terra text-white") : "text-stone-600"}`}>{l}</button>
            ))}
          </div>
          <div><Label className="mb-2 block">Photos (8 max)</Label><PhotoUploader value={f.photos} onChange={set("photos")} testid="publish-photos" /></div>
          <div><Label>Titre</Label><Input data-testid="publish-title-input" value={f.title} onChange={set("title")} maxLength={90} placeholder="Ex. Chaise bistrot en bois" className="bg-white" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Catégorie</Label><SimpleSelect testid="publish-category-select" value={f.category} onChange={set("category")} options={meta.categories} placeholder="Choisir" /></div>
            <div><Label>État</Label><SimpleSelect testid="publish-condition-select" value={f.condition} onChange={set("condition")} options={meta.conditions} placeholder="Choisir" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="publish-description-input" rows={5} value={f.description} onChange={set("description")} placeholder="Dimensions, défauts, conditions de remise…" className="bg-white" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            {f.type === "sale" ? (
              <div><Label>Prix (€)</Label><Input data-testid="publish-price-input" type="number" min="1" value={f.price} onChange={set("price")} className="bg-white" /></div>
            ) : (
              <div className="rounded-xl bg-terra/10 text-terra text-sm p-3">Don à 0 € — toute demande de paiement est interdite.</div>
            )}
            <div><Label>Poids approximatif (kg)</Label><Input data-testid="publish-weight-input" type="number" value={f.weight_kg} onChange={set("weight_kg")} className="bg-white" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Ville</Label><CitySelect testid="publish-city-select" value={f.city} onChange={set("city")} /></div>
            <div><Label>Adresse exacte (privée)</Label><Input data-testid="publish-address-input" value={f.exact_address} onChange={set("exact_address")} placeholder="Visible après réservation acceptée" className="bg-white" /></div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button data-testid="publish-preview-btn" variant="outline" className="rounded-full" onClick={() => setPreview(!preview)}><Eye className="w-4 h-4 mr-2" />Aperçu</Button>
            <Button data-testid="publish-submit-btn" disabled={busy} onClick={submit} className="rounded-full bg-forest hover:bg-forest/90"><Send className="w-4 h-4 mr-2" />{id ? "Enregistrer" : "Publier"}</Button>
          </div>
          <p className="text-xs text-stone-500">Les annonces passent un contrôle anti-fraude automatique (objets interdits, liens externes). En cas de doute, elles sont vérifiées par la modération.</p>
        </div>
        <div className="lg:col-span-5">
          <p className="eyebrow mb-4">Aperçu</p>
          <div className="max-w-xs" data-testid="publish-preview">
            <ListingCard l={{ id: "preview", title: f.title || "Titre de l'annonce", photos: f.photos, type: f.type, price: f.price || 0, city: f.city || "Ville", condition: f.condition || "État", status: "active" }} />
          </div>
          {preview && <p className="mt-4 text-sm text-stone-600 whitespace-pre-line">{f.description}</p>}
        </div>
      </div>
    </PageShell>
  );
}
